import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    BrainCircuit,
    MessagesSquare,
    Rocket,
    BarChart3,
    Users2,
    Wand2,
    Gauge,
    DollarSign,
    TrendingUp,
    ScanLine,
    FileText,
} from "lucide-react";

/* ------------------------------------------------------------------
 * XerooConversationLanding – v8
 * • Navbar transparente (logo + CTA blancos) y sólido al hacer scroll
 * • Hero: fondo bajado a opacity-15, CTA blanco con texto violeta
 * • Logo Tiendanube reducido y mayor separación respecto al texto
 * ------------------------------------------------------------------ */

const BRAND = "#5860D5";

export default function XerooConversationLanding() {
    /* Parallax para la ilustración de fondo */
    const { scrollY } = useScroll();
    const yHero = useTransform(scrollY, [0, 600], [0, 300]);

    /* Estado para navbar */
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 80);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    /* Conversación */
    const conversation = [
        { q: "¿Qué hacen en Xeroo?", a: "Creamos soluciones de Inteligencia Artificial que automatizan tareas, optimizan procesos y generan valor medible en semanas." },
        { q: "¿Cómo pueden ayudarme?", a: "Detectamos oportunidades de alto impacto, diseñamos un plan y te acompañamos hasta demostrar ROI positivo." },
        { q: "¿Cuánto tarda un proyecto típico?", a: "Un MVP funcional suele estar listo en 2-4 semanas; luego iteramos y escalamos." },
        { q: "¿Necesito grandes volúmenes de datos?", a: "¡Tranquilo! Podemos arrancar incluso con datasets pequeños combinando tus datos con modelos pre-entrenados." },
        { q: "¿Cómo empiezo?", a: "Agendá una reunión de descubrimiento y recibí un roadmap de IA personalizado." },
    ];

    /* Beneficios */
    const benefits = [
        { icon: <BrainCircuit className="h-6 w-6 text-white" />, text: "Atención al cliente 24/7 con chatbots IA" },
        { icon: <BarChart3 className="h-6 w-6 text-white" />, text: "Pronósticos de demanda confiable para optimizar inventarios" },
        { icon: <Users2 className="h-6 w-6 text-white" />, text: "Segmentación inteligente de clientes mediante análisis de comportamiento" },
        { icon: <Wand2 className="h-6 w-6 text-white" />, text: "Generación automática de contenido" },
        { icon: <Gauge className="h-6 w-6 text-white" />, text: "Optimización de rutas logísticas y entregas" },
        { icon: <DollarSign className="h-6 w-6 text-white" />, text: "Precios dinámicos que maximizan márgenes" },
        { icon: <TrendingUp className="h-6 w-6 text-white" />, text: "Análisis de sentimiento en redes sociales" },
        { icon: <ScanLine className="h-6 w-6 text-white" />, text: "Detección de oportunidades de Upselling y Crosselling" },
        { icon: <BrainCircuit className="h-6 w-6 text-white" />, text: "Personalización de campañas de marketing automáticamente" },
        { icon: <BarChart3 className="h-6 w-6 text-white" />, text: "Reportes financieros inteligentes al instante" },
        { icon: <FileText className="h-6 w-6 text-white" />, text: "Clasificación y respuesta de solicitudes automáticamente" },
        { icon: <Users2 className="h-6 w-6 text-white" />, text: "Soporte interno a la operación con chatbots internos" }
    ];
    /* Casos de éxito */
    const cases = [
        { sector: "Retail", metric: "-18 % costos logísticos", time: "6 semanas" },
        { sector: "eCommerce", metric: "+35 % conversión", time: "4 semanas" },
        { sector: "Industrias", metric: "+22 % eficiencia energética", time: "7 semanas" },
    ];

    /* Chat bubble helper */
    const ChatBubble = ({ children, me, i }) => (
        <motion.div
            initial={{ opacity: 0, x: me ? 60 : -60, y: 20 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: i * 0.25, duration: 0.6 }}
            className={`flex ${me ? "justify-end" : "justify-start"}`}
        >
            <div className={`max-w-md rounded-2xl p-4 md:p-5 shadow-xl backdrop-blur-sm text-base leading-snug ${me ? "bg-[#5860D5] text-white rounded-br-none" : "bg-white/70 rounded-bl-none"}`}>
                {children}
            </div>
        </motion.div>
    );

    /* Render */
    return (
        <div className="min-h-screen w-full bg-gradient-to-b from-[#fafafe] via-[#f4f6ff] to-white">
            {/* Navbar */}
            <header className={`fixed top-0 w-full z-30 transition-colors duration-300 ${scrolled ? "backdrop-blur-lg bg-white/60 ring-1 ring-white/20 shadow-[0_0_18px_rgba(88,96,213,0.25)]" : "bg-transparent"}`}>
                <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 py-4">
                    {/* Logo cambia de color según scroll */}
                    <img src="/xeroo-logo-violet.png" alt="Xeroo" className={`h-10 transition-filter ${scrolled ? "" : "filter brightness-0 invert"}`} />
                    {/* CTA: blanco con texto violeta en modo transparente */}
                    <a href={"https://form.typeform.com/to/pQ4eEucT"} target="_blank" rel="noopener" className={`hidden md:inline-flex rounded-full px-5 py-2 text-sm font-medium shadow-lg hover:scale-105 transition-transform ${scrolled ? "bg-[#5860D5] text-white" : "bg-white text-[#5860D5]"}`}>
                        Agendar reunión
                    </a>
                </div>
            </header>

            {/* Hero fullscreen */}
            <section id="start" className="relative overflow-hidden min-h-screen flex flex-col justify-center items-center text-center px-6 select-none">
                <motion.img src="../heroTech_ai.png" alt="Fondo IA" style={{ y: yHero }} className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
                <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative z-10 text-4xl md:text-6xl font-extrabold leading-tight bg-gradient-to-r from-[#6a71ff] via-[#5860D5] to-[#a165ff] bg-clip-text text-transparent">
                    Inteligencia Artificial <br />que impulsa tu negocio
                </motion.h1>
                <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className="relative z-10 mt-6 max-w-2xl text-lg md:text-xl text-gray-200 md:text-gray-100 drop-shadow">
                    Soluciones de IA para generar crecimiento y eficiencia en <span className="font-semibold">semanas</span>. Automatización y optimización al alcance de tu empresa.
                </motion.p>
                <motion.a href={"https://form.typeform.com/to/pQ4eEucT"} target="_blank" rel="noopener" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }} className="relative z-10 mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#5860D5] font-semibold shadow-lg hover:scale-105 active:scale-95 transition-transform">
                    <Rocket className="h-5 w-5" /> Agendar reunión
                </motion.a>
            </section>

            {/* Conversación */}
            <section id="conversation" className="mt-28 flex flex-col items-center px-4">
                <div className="max-w-3xl w-full space-y-6">
                    {conversation.map((item, idx) => (
                        <React.Fragment key={idx}>
                            <ChatBubble me={false} i={idx * 2}>{item.q}</ChatBubble>
                            <ChatBubble me={true} i={idx * 2 + 1}>{item.a}</ChatBubble>
                        </React.Fragment>
                    ))}
                </div>
            </section>

            {/* Beneficios */}
            <section id="benefits" className="mt-32 bg-white py-20">
                <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-3 bg-gradient-to-r from-[#6a71ff] via-[#5860D5] to-[#a165ff] bg-clip-text text-transparent">Beneficios concretos</h2>
                <p className="text-center text-lg text-gray-600 mb-12">Resultados que podés medir desde la primera entrega</p>
                <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {benefits.map((b) => (
                        <motion.div key={b.text} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }} className="flex items-start gap-4 bg-[#5860D5] p-6 rounded-2xl shadow-xl text-white hover:shadow-2xl">
                            <div className="p-3 rounded-full bg-white/15">{b.icon}</div>
                            <p className="leading-snug flex-1">{b.text}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Casos de éxito */}
            <section id="cases" className="bg-gradient-to-r from-[#eff2ff] to-white py-20">
                <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-3">Casos de éxito</h2>
                <p className="text-center text-lg text-gray-600 mb-10">Empresas que aceleraron su retorno con Inteligencia Artificial</p>
                <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8 px-6">
                    {cases.map((c) => (
                        <motion.div key={c.sector} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }} className="bg-white p-8 rounded-2xl shadow-xl text-center flex flex-col">
                            <span className="text-[#5860D5] font-semibold tracking-wide uppercase">{c.sector}</span>
                            <span className="mt-4 text-2xl font-bold">{c.metric}</span>
                            <span className="mt-2 text-sm text-gray-500">en {c.time}</span>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Marcas con las que trabajamos */}
            <section className="bg-white py-16">
                <h2 className="text-center text-2xl md:text-3xl font-extrabold mb-12">Marcas con las que trabajamos</h2>
                <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-10 px-6">
                    {[
                        { src: "/logos/salesforce-logo.png", alt: "Salesforce" },
                        { src: "/logos/aws-logo.png", alt: "AWS" },
                        { src: "/logos/meta-logo.png", alt: "Meta" },
                        { src: "/logos/openai-logo.png", alt: "OpenAI" },
                    ].map(({ src, alt }) => (
                        <div key={alt} className="w-32 h-10 flex items-center justify-center">
                            <img src={src} alt={alt} className="max-h-full max-w-full object-contain" />
                        </div>
                    ))}
                </div>
            </section>

            {/* Banner Tiendanube Partner */}
            <section className="bg-[#f5f7ff] py-16">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-20 px-6">
                    <img src="/logos/tiendanube-logo.png" alt="Tiendanube Partner" className="h-16" />
                    <p className="text-lg text-gray-700 leading-relaxed max-w-2xl">
                        Como partners de Tiendanube, integramos IA que potencia tu e-commerce: recomendaciones
                        inteligentes, automatización de atención y análisis predictivo de ventas para escalar tu
                        negocio sin fricción.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section id="cta" className="relative py-28 overflow-hidden">
                <motion.div initial={{ scale: 0.9, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="max-w-4xl mx-auto bg-[#5860D5] text-white rounded-3xl shadow-2xl px-10 py-14 flex flex-col md:flex-row items-center gap-6">
                    <MessagesSquare className="h-16 w-16 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-2xl md:text-3xl font-bold leading-snug">¿Listo para aprovechar la IA?</h3>
                        <p className="mt-2 text-lg opacity-90">Agendá una reunión estratégica gratuita con nuestros expertos.</p>
                    </div>
                    <a href={"https://form.typeform.com/to/pQ4eEucT"} target="_blank" rel="noopener" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#5860D5] font-semibold shadow-lg hover:scale-105 transition-transform whitespace-nowrap">
                        <Rocket className="h-5 w-5" /> Agendar reunión
                    </a>
                </motion.div>
            </section>

            {/* Footer */}
            <footer className="bg-white text-center text-sm py-10 text-gray-600 space-y-2">
                <div>Xeroo LLC – 131 Continental Dr, Newark, Delaware</div>
                <div>
                    Tel/WhatsApp:{" "}
                    <a href="https://wa.me/5491141624731" className="underline hover:text-[#5860D5]" target="_blank" rel="noopener">
                        +54 9 11 4162 4731
                    </a>
                </div>
                <div>
                    <a href="https://linkedin.com/company/xeroo-ai" className="underline hover:text-[#5860D5] mr-4" target="_blank" rel="noopener">LinkedIn</a>
                    <a href="https://instagram.com/xeroo.ai" className="underline hover:text-[#5860D5]" target="_blank" rel="noopener">Instagram</a>
                </div>
                <div className="text-gray-500">
                    © {new Date().getFullYear()} Xeroo. Todos los derechos reservados.
                </div>
            </footer>
        </div>
    );
}
